import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { mutations, queries, queryKeys } from 'RestAPIs/rapini';

import { Button, DialogActions, TextField } from '@mui/material';

import {
  Dialog,
  showSnackbar,
  VirtualizedAutocomplete
} from 'components/shared';

type Props = {
  title: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  regionalPoolFunctionId: number | undefined;
  setRegionalPoolFunctionId: (
    regionalPoolFunctionId: number | undefined
  ) => void;
  regionalPoolTenantName: string;
  tenantFunctionId: number;
};

export default function MappingFunctionsDialog({
  title,
  open,
  setOpen,
  regionalPoolFunctionId,
  setRegionalPoolFunctionId,
  regionalPoolTenantName,
  tenantFunctionId
}: Props) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  const { data } = queries.useGetRegionalPoolFunctions(
    regionalPoolTenantName as string,
    {
      enabled: !!regionalPoolTenantName
    }
  );

  const options =
    data
      ?.map((r) => ({
        id: r.id as number,
        name: r.description as string
      }))
      .sort(function (a, b) {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      }) ?? [];

  const { mutate } = mutations.useUpdateRegionalPoolFunctionMapping(
    regionalPoolTenantName as string,
    tenantFunctionId as number
  );

  function onSaveClick() {
    mutate(
      { regionalPoolFunctionId },
      {
        onError: () => {
          showSnackbar({
            severity: 'error',
            message: t('somethingWentWrong')
          });
        },
        onSuccess: () => {
          queryClient.invalidateQueries(
            queryKeys.getRegionalPoolFunctionMappings(regionalPoolTenantName)
          );
          setOpen(false);
        }
      }
    );
  }
  return (
    <Dialog title={title} open={open} handleClose={() => setOpen(false)}>
      <VirtualizedAutocomplete
        autoSelect
        options={options}
        value={options?.find((o) => o.id === regionalPoolFunctionId) ?? null}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option;
          }
          return option.name as string;
        }}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        renderInput={(params) => (
          <TextField {...params} label={t('selectFunction')} />
        )}
        onChange={(e, value: any) => {
          setRegionalPoolFunctionId(value?.id ?? undefined);
        }}
      />
      <DialogActions className="dialog-actions">
        <Button
          onClick={() => setOpen(false)}
          color="secondary"
          variant="contained"
        >
          {t('cancel')}
        </Button>
        <Button onClick={onSaveClick} color="primary" variant="contained">
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
